import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Button,
  ConfigProvider,
  Dropdown,
  Form,
  Menu,
  message,
  Modal,
  Pagination,
  Table,
  Tag,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useThemeMode } from "../../../contexts/ThemeContext";
import { getLocalAntdTheme } from "../../../theme/antdTheme";
import CouponFormModal from "./CouponFormModal";
import UsageStatsModal from "./UsageStatsModal";
import { Wrapper } from "./style";
import {
  buildCouponPayload,
  couponToFormValues,
  getDefaultCouponFormValues,
} from "./lib/couponHelpers";
import {
  createCoupon,
  deleteCoupon,
  fetchAvailablePlans,
  fetchCoupon,
  fetchCoupons,
  updateCoupon,
} from "./lib/api";

const formatDateTime = (unix) => {
  const ts = parseInt(unix, 10);
  if (!ts) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(ts * 1000));
};

const formatCycles = (cycles) => {
  if (!Array.isArray(cycles) || !cycles.length) return "—";
  return cycles
    .map((cycle) =>
      cycle === "yearly" ? "Yearly" : cycle === "monthly" ? "Monthly" : cycle
    )
    .join(", ");
};

const formatApplicablePlans = (coupon) => {
  const details = coupon?.applicable_plans_details;
  if (Array.isArray(details) && details.length) {
    return details.map((plan) => plan?.name || plan?.id).filter(Boolean);
  }
  const planIds = coupon?.applicable_plans;
  if (Array.isArray(planIds) && planIds.length) {
    return planIds.map(String);
  }
  return [];
};

export default function BillingCoupons({ userData }) {
  const { themeMode } = useThemeMode();
  const token = userData?.token;

  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [form] = Form.useForm();

  const [statsOpen, setStatsOpen] = useState(false);
  const [statsCouponId, setStatsCouponId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadCoupons = useCallback(
    async (override = {}) => {
      if (!token) return;
      setLoading(true);
      try {
        const data = await fetchCoupons(
          {
            page: override.page ?? page,
            perPage: override.perPage ?? perPage,
          },
          token
        );
        setCoupons(data.coupons);
        setTotal(data.pagination.total || 0);
        setPage(data.pagination.page || override.page || page);
        setPerPage(data.pagination.per_page || override.perPage || perPage);
      } catch (err) {
        message.warning(err?.message || "Failed to load coupons");
        setCoupons([]);
      } finally {
        setLoading(false);
      }
    },
    [token, page, perPage]
  );

  const loadAvailablePlans = useCallback(async () => {
    if (!token) return;
    setPlansLoading(true);
    try {
      const plans = await fetchAvailablePlans(token);
      setAvailablePlans(plans);
    } catch (err) {
      message.warning(err?.message || "Failed to load available plans");
      setAvailablePlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadCoupons({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const closeFormModal = () => {
    setFormOpen(false);
    setEditingId(null);
    setFormLoading(false);
    form.resetFields();
  };

  const openCreateModal = () => {
    form.resetFields();
    form.setFieldsValue(getDefaultCouponFormValues());
    setEditingId(null);
    setFormOpen(true);
    loadAvailablePlans();
  };

  const openEditModal = async (couponId) => {
    setEditingId(couponId);
    setFormOpen(true);
    setFormLoading(true);
    loadAvailablePlans();
    try {
      const coupon = await fetchCoupon(couponId, token);
      form.setFieldsValue(couponToFormValues(coupon));
    } catch (err) {
      message.warning(err?.message || "Failed to load coupon");
      closeFormModal();
    } finally {
      setFormLoading(false);
    }
  };

  const openStatsModal = (couponId) => {
    setStatsCouponId(couponId);
    setStatsOpen(true);
  };

  const handleSubmitCoupon = async (values) => {
    setSaving(true);
    try {
      const payload = buildCouponPayload(values);
      const response = editingId
        ? await updateCoupon(editingId, payload, token)
        : await createCoupon(payload, token);
      message.success(
        response?.message ||
          (editingId ? "Coupon updated successfully" : "Coupon created successfully")
      );
      closeFormModal();
      loadCoupons({ page: editingId ? page : 1 });
    } catch (err) {
      message.warning(
        err?.message ||
          (editingId ? "Failed to update coupon" : "Failed to create coupon")
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCoupon = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const response = await deleteCoupon(deleteTarget.id, token);
      message.success(response?.message || "Coupon deleted successfully");
      setDeleteTarget(null);
      loadCoupons({ page });
    } catch (err) {
      message.warning(err?.message || "Failed to delete coupon");
    } finally {
      setDeleting(false);
    }
  };

  const renderRowMenu = (row) => (
    <Menu className="min-w-200px p-3 billing-coupons-action-menu">
      <Menu.Item
        key="edit"
        className="d-flex align-items-center mb-2"
        onClick={() => openEditModal(row.id)}
      >
        <i className="ki-outline ki-pencil fs-3 me-2" />
        Edit
      </Menu.Item>
      <Menu.Item
        key="stats"
        className="d-flex align-items-center mb-2"
        onClick={() => openStatsModal(row.id)}
      >
        <i className="ki-outline ki-chart-line fs-3 me-2" />
        Usage stats
      </Menu.Item>
      <Menu.Item
        key="delete"
        className="d-flex align-items-center"
        onClick={() => setDeleteTarget(row)}
      >
        <i className="ki-outline ki-trash fs-3 me-2 text-danger" />
        <span className="text-danger">Delete</span>
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: "#",
      key: "index",
      width: 60,
      render: (_, __, index) => (page - 1) * perPage + index + 1,
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code) => <span className="billing-coupons-code">{code}</span>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) => name || "—",
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, row) => (
        <Tag color="processing" className="billing-coupons-plan-tag">
          {row.formatted_discount || `${row.discount_value}%`}
        </Tag>
      ),
    },
    {
      title: "Usage",
      key: "usage",
      render: (_, row) => {
        const used = row.usage_count ?? 0;
        const limit = row.usage_limit;
        return limit != null ? `${used} / ${limit}` : used;
      },
    },
    {
      title: "Cycles",
      key: "applicable_cycles",
      render: (_, row) => formatCycles(row.applicable_cycles),
    },
    {
      title: "Plans",
      key: "applicable_plans",
      render: (_, row) => {
        const plans = formatApplicablePlans(row);
        if (!plans.length) return <span className="text-muted">All plans</span>;
        return (
          <div className="billing-coupons-plan-tags">
            {plans.map((plan) => (
              <Tag key={plan} className="billing-coupons-plan-tag">
                {plan}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      render: (isActive) =>
        isActive === 1 ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag color="default">Inactive</Tag>
        ),
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: formatDateTime,
    },
    {
      title: "Action",
      key: "actions",
      fixed: "right",
      width: 80,
      align: "center",
      render: (_, row) => (
        <Dropdown overlay={renderRowMenu(row)} trigger={["click"]}>
          <Button type="link" className="billing-coupons-action-trigger">
            <i className="ki-solid ki-dots-vertical fs-1 billing-coupons-action-icon" />
          </Button>
        </Dropdown>
      ),
    },
  ];

  const theme = getLocalAntdTheme(themeMode, "#f45b68");

  const planOptions = useMemo(
    () =>
      availablePlans.map((plan) => ({
        value: plan.id,
        label: plan.name,
        searchText: [plan.name, plan.description]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      })),
    [availablePlans]
  );

  return (
    <Wrapper>
      <Helmet>
        <title>Billing Coupons</title>
      </Helmet>

      <div className="billing-coupons-header">
        <div>
          <div className="billing-coupons-title">Billing Coupons</div>
          <div className="billing-coupons-subtitle">
            Create and manage discount coupons for subscription plans.
          </div>
        </div>
      </div>

      <div className="billing-coupons-toolbar">
        <Button icon={<ReloadOutlined />} onClick={() => loadCoupons()} loading={loading}>
          Refresh
        </Button>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
          className="fs-7 fw-bold"
          style={{ background: "#f45b68", borderColor: "#f45b68" }}
        >
          Create Coupon
        </Button>
      </div>

      <div className="card card-xxl-stretch mb-5 mb-xl-8">
        <div className="card-body py-3">
          <ConfigProvider theme={theme}>
            <Table
              rowKey="id"
              loading={loading}
              columns={columns}
              dataSource={coupons}
              pagination={false}
              scroll={{ x: 1300 }}
              className="billing-coupons-table"
              components={{
                header: {
                  cell: (props) => (
                    <th
                      {...props}
                      className={`${props.className || ""} billing-coupons-table-header-row`}
                    />
                  ),
                },
              }}
            />
            {total > 0 && (
              <div className="d-flex justify-content-end mt-4">
                <Pagination
                  current={page}
                  pageSize={perPage}
                  total={total}
                  showSizeChanger
                  pageSizeOptions={[10, 20, 50]}
                  onChange={(nextPage, nextSize) => {
                    setPage(nextPage);
                    setPerPage(nextSize);
                    loadCoupons({ page: nextPage, perPage: nextSize });
                  }}
                />
              </div>
            )}
          </ConfigProvider>
        </div>
      </div>

      <ConfigProvider theme={theme}>
        <CouponFormModal
          open={formOpen}
          editingId={editingId}
          form={form}
          formLoading={formLoading}
          saving={saving}
          plansLoading={plansLoading}
          planOptions={planOptions}
          onCancel={closeFormModal}
          onSubmit={handleSubmitCoupon}
        />

        <UsageStatsModal
          open={statsOpen}
          couponId={statsCouponId}
          token={token}
          onClose={() => {
            setStatsOpen(false);
            setStatsCouponId(null);
          }}
        />

        <Modal
          className="billing-coupons-modal"
          title="Delete coupon"
          open={Boolean(deleteTarget)}
          onCancel={() => !deleting && setDeleteTarget(null)}
          centered
          footer={[
            <button
              key="cancel"
              type="button"
              className="btn btn-light fs-7 fw-bold me-2"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </button>,
            <button
              key="delete"
              type="button"
              className="btn btn-danger fs-7 fw-bold text-white"
              onClick={handleDeleteCoupon}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </button>,
          ]}
        >
          <p className="billing-coupons-delete-text mb-0">
            Delete coupon <strong>{deleteTarget?.code}</strong>? This cannot be undone.
          </p>
        </Modal>
      </ConfigProvider>
    </Wrapper>
  );
}
